import { Fragment, useState, useEffect, useCallback } from 'react'
import { supabase } from '../../../api/supabase'
import CustomerCombobox from '../../../components/CustomerCombobox'
import { useCustomersByRole } from '../../../hooks/useCustomersByRole'
import type {
  PricingRateCard, PricingRateLine, PricingRateLineCost, PricingCategory, PricingLane, PricingChargeCode, PricingModeScopedOption, RateCardStatus, RateLineUnit,
} from './types'
import { RATE_LINE_UNIT_OPTIONS, RATE_CARD_STATUS_OPTIONS } from './types'
import { useStickyHScroll } from '../../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../../components/StickyHScrollbar'
import TierEditor from '../../../components/pricing/TierEditor'
import { loadTiers, saveTiers, validateTiers, type TierDraft } from '../../../lib/pricingTiers'
import { RateLineExtraHeaders, RateLineExtraCells } from '../../../components/pricing/RateLineExtraCells'
import { blankLineFlags } from '../../../lib/rateLineFields'
import CardPortsPicker from '../../../components/pricing/CardPortsPicker'
import { loadCardPorts, saveCardPorts, type PortOption } from '../../../lib/cardPorts'
import { NewFromTemplateDialog, CloneCardDialog } from './QuoteCardDialogs'

const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'
const cellInputCls = 'w-full min-w-[5rem] border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'

// Charge Code column is pinned to the left so a row stays identifiable while the table is scrolled
// sideways. Needs its own background (cells scroll underneath it) and a divider shadow.
const stickyCodeCell = 'sticky left-0 z-10 bg-white dark:bg-gray-800 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.2)]'

interface ContainerType { id: string; code: string }

// One line item, plus its cost (kept as a sibling field here for editing convenience -- written
// to the separate pricing_rate_line_costs table on save, per the RLS split, see the schema
// migration's file header for why unit_cost can't just live on pricing_rate_lines).
interface LineRow extends Omit<PricingRateLine, 'id' | 'rate_card_id'> {
  id?: string
  unit_cost: number | null
  tiers: TierDraft[] // pricing_rate_tiers, edited in place (TierEditor)
  _hadTiers: boolean
  _key: string // stable React key for new (id-less) rows
}

interface AuditEntry {
  id: string
  operation: string
  user_email: string | null
  changed_fields: string[] | null
  created_at: string
}

function emptyCardForm(): Omit<PricingRateCard, 'id' | 'created_at' | 'modified_at' | 'created_by'> {
  return {
    customer_id: null, mode_id: '', clearance_type_id: null, delivery_method_id: null, lane_id: null, rate_card_name: '',
    effective_from: new Date().toISOString().slice(0, 10), effective_to: null,
    currency: 'GBP', status: 'DRAFT', is_public_to_customer: true, notes: '',
  }
}

function emptyLine(): LineRow {
  return { charge_code_id: '', unit: 'flat', container_type_id: null, unit_price: null, unit_cost: null, min_charge: null, max_charge: null, notes: '', ...blankLineFlags(), tiers: [], _hadTiers: false, _key: crypto.randomUUID() }
}

export default function RateCardsTab() {
  const [view, setView] = useState<'list' | 'detail'>('list')
  const [cards, setCards] = useState<PricingRateCard[]>([])
  const [categories, setCategories] = useState<PricingCategory[]>([])
  const [lanes, setLanes] = useState<PricingLane[]>([])
  const [chargeCodes, setChargeCodes] = useState<PricingChargeCode[]>([])
  const [clearanceTypes, setClearanceTypes] = useState<PricingModeScopedOption[]>([])
  const [deliveryMethods, setDeliveryMethods] = useState<PricingModeScopedOption[]>([])
  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([])
  const [loading, setLoading] = useState(true)

  // List filters
  const [filterCustomer, setFilterCustomer] = useState('')
  const [filterMode, setFilterMode] = useState('')
  const [effectiveTodayOnly, setEffectiveTodayOnly] = useState(false)
  const { customers } = useCustomersByRole('Cus')

  // Detail state
  const [cardId, setCardId] = useState<string | null>(null)
  const [cardForm, setCardForm] = useState<ReturnType<typeof emptyCardForm> | null>(null)
  const [lines, setLines] = useState<LineRow[]>([])
  const [audit, setAudit] = useState<AuditEntry[]>([])
  const [ports, setPorts] = useState<PortOption[]>([])
  const [savedStatus, setSavedStatus] = useState<RateCardStatus | null>(null)
  const [tiersOpen, setTiersOpen] = useState<Set<string>>(new Set())
  const [showTemplateDialog, setShowTemplateDialog] = useState(false)
  const [cloneSource, setCloneSource] = useState<PricingRateCard | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Cost is internal-only and rarely typed while pricing a customer card, so its column starts
  // collapsed (values stay in state and are saved either way).
  const [showCost, setShowCost] = useState(false)
  const { scrollProps: lineScrollProps, contentRef: lineTableRef, bar: lineScrollBar } = useStickyHScroll([lines, tiersOpen])

  const loadLookups = useCallback(async () => {
    const [{ data: cats }, { data: laneRows }, { data: codes }, { data: ctypes }, { data: clearances }, { data: methods }] = await Promise.all([
      supabase.from('pricing_categories').select('*').eq('is_active', true).order('display_order'),
      supabase.from('pricing_lanes').select('*').eq('is_active', true).order('name'),
      supabase.from('pricing_charge_codes').select('*').eq('is_active', true).order('code'),
      supabase.from('container_types').select('id, code').eq('is_active', true).order('display_order'),
      supabase.from('pricing_clearance_types').select('*').eq('is_active', true).order('name'),
      supabase.from('pricing_delivery_methods').select('*').eq('is_active', true).order('name'),
    ])
    setCategories((cats as PricingCategory[]) || [])
    setLanes((laneRows as PricingLane[]) || [])
    setChargeCodes((codes as PricingChargeCode[]) || [])
    setContainerTypes((ctypes as ContainerType[]) || [])
    setClearanceTypes((clearances as PricingModeScopedOption[]) || [])
    setDeliveryMethods((methods as PricingModeScopedOption[]) || [])
  }, [])

  const loadCards = useCallback(async () => {
    setLoading(true)
    let query = supabase.from('pricing_rate_cards').select('*').order('rate_card_name')
    if (filterCustomer) query = query.eq('customer_id', filterCustomer)
    if (filterMode) query = query.eq('mode_id', filterMode)
    if (effectiveTodayOnly) {
      const today = new Date().toISOString().slice(0, 10)
      query = query.lte('effective_from', today).or(`effective_to.is.null,effective_to.gte.${today}`)
    }
    const { data } = await query
    setCards((data as PricingRateCard[]) || [])
    setLoading(false)
  }, [filterCustomer, filterMode, effectiveTodayOnly])

  useEffect(() => { loadLookups() }, [loadLookups])
  useEffect(() => { if (view === 'list') loadCards() }, [view, loadCards])

  const customerName = (id: string | null) => id ? (customers.find(c => c.id === id)?.name || '—') : 'All customers (default)'
  const modeName = (id: string) => categories.find(c => c.id === id)?.name_en || '—'
  const optionName = (opts: PricingModeScopedOption[], id: string | null) => id ? (opts.find(o => o.id === id)?.name || '—') : '—'
  const laneName = (id: string | null) => id ? (lanes.find(l => l.id === id)?.name || '—') : 'All lanes'

  const openNew = () => {
    setCardId(null)
    setCardForm(emptyCardForm())
    setLines([emptyLine()])
    setPorts([]); setSavedStatus(null); setTiersOpen(new Set())
    setAudit([])
    setError(null)
    setView('detail')
  }

  const openCard = async (id: string) => {
    setError(null)
    const [{ data: card }, { data: lineRows }, { data: auditRows }] = await Promise.all([
      supabase.from('pricing_rate_cards').select('*').eq('id', id).single(),
      supabase.from('pricing_rate_lines').select('*').eq('rate_card_id', id).order('id'),
      supabase.from('audit_logs').select('id, operation, user_email, changed_fields, created_at')
        .eq('table_name', 'pricing_rate_cards').eq('record_id', id).order('created_at', { ascending: false }).limit(20),
    ])
    if (!card) { setError('Rate card not found.'); return }
    const lineIds = ((lineRows as PricingRateLine[]) || []).map(l => l.id)
    const { data: costRows } = lineIds.length > 0
      ? await supabase.from('pricing_rate_line_costs').select('*').in('rate_line_id', lineIds)
      : { data: [] as PricingRateLineCost[] }
    const costByLine = new Map((costRows || []).map(c => [c.rate_line_id, c.unit_cost]))

    let tiersByLine = new Map<string, TierDraft[]>()
    try { tiersByLine = await loadTiers('pricing_rate_tiers', lineIds) } catch (e) { setError(e instanceof Error ? e.message : 'Could not load tiers') }
    let cardPorts: PortOption[] = []
    try { cardPorts = await loadCardPorts('pricing_rate_card_ports', id) } catch { /* migration 20261003 not run yet: no ports */ }

    setCardId(id)
    setCardForm(card as PricingRateCard)
    setSavedStatus((card as PricingRateCard).status)
    setPorts(cardPorts)
    setTiersOpen(new Set())
    setLines((((lineRows as PricingRateLine[]) || []).map(l => {
      const tiers = tiersByLine.get(l.id) || []
      return { ...l, unit_cost: costByLine.get(l.id) ?? null, tiers, _hadTiers: tiers.length > 0, _key: l.id }
    })))
    setAudit((auditRows as AuditEntry[]) || [])
    setView('detail')
  }

  const addLine = () => setLines(ls => [...ls, { ...emptyLine(), scope: cardForm?.delivery_method_id ? 'DELIVERY' : 'CONTAINER' }])
  const toggleTiers = (key: string) => setTiersOpen(st => { const n = new Set(st); if (n.has(key)) n.delete(key); else n.add(key); return n })
  const removeLine = (key: string) => setLines(ls => ls.filter(l => l._key !== key))
  const updateLine = (key: string, patch: Partial<LineRow>) => setLines(ls => ls.map(l => l._key === key ? { ...l, ...patch } : l))

  const save = async (publish: boolean) => {
    if (!cardForm) return
    if (!cardForm.mode_id || !cardForm.rate_card_name.trim() || !cardForm.effective_from) {
      setError('Mode, Rate Card Name, and Effective From are required.'); return
    }
    if (!cardForm.delivery_method_id && lines.some(l => l.scope === 'DELIVERY')) {
      setError('This card has DELIVERY lines, so it needs a Delivery Method. Set it, or change those lines\' scope.'); return
    }
    if (lines.some(l => !l.charge_code_id)) { setError('Every line needs a Charge Code.'); return }
    for (const l of lines) {
      const tierErr = validateTiers(l.tiers)
      if (tierErr) { setError(`${chargeCodes.find(c => c.id === l.charge_code_id)?.code || 'Line'}: ${tierErr}`); return }
    }
    const wantStatus: RateCardStatus = publish ? 'ACTIVE' : cardForm.status
    if (wantStatus === 'ACTIVE') {
      const unpriced = lines.filter(l => l.is_enabled && !l.is_at_cost && l.unit_price == null)
      if (unpriced.length > 0) { setError(`Cannot publish: ${unpriced.length} enabled line(s) have no price (${unpriced.map(l => chargeCodes.find(c => c.id === l.charge_code_id)?.code).join(', ')}). Price them, mark them at-cost, or switch them off.`); return }
    }
    setSaving(true); setError(null)
    try {
      // Becoming ACTIVE is done last, after the lines are saved: the DB refuses to activate a card whose
      // stored lines are still unpriced, and the prices typed here are not stored yet.
      const payload = { ...cardForm, delivery_method_id: cardForm.delivery_method_id || null, status: wantStatus === 'ACTIVE' ? (savedStatus ?? 'DRAFT') : wantStatus }
      let savedCardId = cardId
      if (savedCardId) {
        const { error: err } = await supabase.from('pricing_rate_cards').update(payload).eq('id', savedCardId)
        if (err) throw err
      } else {
        const { data, error: err } = await supabase.from('pricing_rate_cards').insert(payload).select('id').single()
        if (err) throw err
        savedCardId = data!.id
      }

      // Lines: upsert existing (have an id), insert new (no id), delete removed.
      const { data: existingLineRows } = await supabase.from('pricing_rate_lines').select('id').eq('rate_card_id', savedCardId)
      const existingIds = new Set((existingLineRows || []).map(r => r.id))
      const keptIds = new Set(lines.filter(l => l.id).map(l => l.id))
      const toDelete = [...existingIds].filter(id => !keptIds.has(id))
      if (toDelete.length > 0) {
        const { error: err } = await supabase.from('pricing_rate_lines').delete().in('id', toDelete)
        if (err) throw err
      }

      for (const line of lines) {
        const { unit_cost, _key, id, tiers, _hadTiers, ...rest } = line
        void _key
        const linePayload = { ...rest, rate_card_id: savedCardId }
        let lineId = id
        if (lineId) {
          const { error: err } = await supabase.from('pricing_rate_lines').update(linePayload).eq('id', lineId)
          if (err) throw err
        } else {
          const { data, error: err } = await supabase.from('pricing_rate_lines').insert(linePayload).select('id').single()
          if (err) throw err
          lineId = data!.id
        }
        if (unit_cost != null) {
          await supabase.from('pricing_rate_line_costs').upsert({ rate_line_id: lineId, unit_cost, updated_at: new Date().toISOString() })
        } else {
          await supabase.from('pricing_rate_line_costs').delete().eq('rate_line_id', lineId)
        }
        if (tiers.length > 0 || _hadTiers) await saveTiers('pricing_rate_tiers', lineId!, tiers)
      }

      await saveCardPorts('pricing_rate_card_ports', savedCardId!, ports)
      if (wantStatus === 'ACTIVE' && payload.status !== 'ACTIVE') {
        const { error: err } = await supabase.from('pricing_rate_cards').update({ status: 'ACTIVE' }).eq('id', savedCardId!)
        if (err) throw err
      }

      await openCard(savedCardId!)
      setView('detail')
    } catch (err: any) {
      setError(err?.message || 'Unknown error')
    } finally {
      setSaving(false)
    }
  }

  // pricing_categories is the Mode table (SEA/AIR/RAIL/TRUCK) since 20260918b. Container type
  // applies to every mode except AIR: sea, rail and truck all arrive as containers/trailers
  // into the bonded warehouse (Gary, 2026-10-01).
  const selectedMode = categories.find(c => c.id === cardForm?.mode_id)
  const showContainerType = !!selectedMode && selectedMode.code !== 'AIR'
  const costCount = lines.filter(l => l.unit_cost != null).length
  const lanesForMode = lanes.filter(l => l.category_id === cardForm?.mode_id)
  // Clearance/delivery options are scoped to the chosen mode via applicable_modes. An option
  // already saved on the card stays visible even if it no longer matches, so editing never
  // silently blanks a stored value.
  const optionsForMode = (opts: PricingModeScopedOption[], selectedId: string | null) =>
    opts.filter(o => o.id === selectedId || (!!selectedMode && o.applicable_modes.includes(selectedMode.code)))

  if (view === 'detail' && cardForm) {
    return (
      <div className="flex flex-col 2xl:flex-row gap-6">
        <div className="flex-1 min-w-0">
          <button onClick={() => setView('list')} className="text-sm text-blue-600 dark:text-blue-400 hover:underline mb-4">&larr; Rate Cards</button>

          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{error}</div>}

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 mb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{cardId ? 'Edit Rate Card' : 'New Rate Card'}</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Customer <span className="text-gray-400 font-normal">(blank = default for all)</span></label>
                <CustomerCombobox value={cardForm.customer_id || ''} onChange={id => setCardForm(f => f && ({ ...f, customer_id: id || null }))} placeholder="— All customers (default rate) —" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mode *</label>
                <select className={inputCls} value={cardForm.mode_id} onChange={e => setCardForm(f => f && ({ ...f, mode_id: e.target.value, lane_id: null, clearance_type_id: null, delivery_method_id: null }))}>
                  <option value="">— Select —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name_en}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Clearance Type <span className="text-gray-400 font-normal">(blank = any)</span></label>
                <select className={inputCls} value={cardForm.clearance_type_id || ''} disabled={!cardForm.mode_id} onChange={e => setCardForm(f => f && ({ ...f, clearance_type_id: e.target.value || null }))}>
                  <option value="">— Any —</option>
                  {optionsForMode(clearanceTypes, cardForm.clearance_type_id).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Delivery Method <span className="text-gray-400 font-normal">(only needed when the card has DELIVERY lines)</span></label>
                <select className={inputCls} value={cardForm.delivery_method_id || ''} disabled={!cardForm.mode_id} onChange={e => setCardForm(f => f && ({ ...f, delivery_method_id: e.target.value || null }))}>
                  <option value="">— None (container / HBL / cargo quote) —</option>
                  {optionsForMode(deliveryMethods, cardForm.delivery_method_id).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lane <span className="text-gray-400 font-normal">(blank = all lanes)</span></label>
                <select className={inputCls} value={cardForm.lane_id || ''} onChange={e => setCardForm(f => f && ({ ...f, lane_id: e.target.value || null }))}>
                  <option value="">— All lanes —</option>
                  {lanesForMode.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ports <span className="text-gray-400 font-normal">(discharge ports this quote is for, e.g. Felixstowe + Southampton)</span></label>
                <CardPortsPicker value={ports} onChange={setPorts} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rate Card Name *</label>
                <input className={inputCls} value={cardForm.rate_card_name} onChange={e => setCardForm(f => f && ({ ...f, rate_card_name: e.target.value }))} placeholder='e.g. "KIMBERLY 海运 2026"' />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Effective From *</label>
                <input type="date" className={inputCls} value={cardForm.effective_from} onChange={e => setCardForm(f => f && ({ ...f, effective_from: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Effective To</label>
                <input type="date" className={inputCls} value={cardForm.effective_to || ''} onChange={e => setCardForm(f => f && ({ ...f, effective_to: e.target.value || null }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Currency</label>
                <input className={inputCls} value={cardForm.currency} onChange={e => setCardForm(f => f && ({ ...f, currency: e.target.value.toUpperCase().slice(0, 3) }))} maxLength={3} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                <select className={inputCls} value={cardForm.status} onChange={e => setCardForm(f => f && ({ ...f, status: e.target.value as RateCardStatus }))}>
                  {RATE_CARD_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <textarea className={inputCls} rows={2} value={cardForm.notes || ''} onChange={e => setCardForm(f => f && ({ ...f, notes: e.target.value }))} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer col-span-2">
                <input type="checkbox" checked={cardForm.is_public_to_customer} onChange={e => setCardForm(f => f && ({ ...f, is_public_to_customer: e.target.checked }))} className="h-4 w-4 text-blue-600 rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Share with customer via portal (future)</span>
              </label>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">Line Items</h3>
              <button type="button" onClick={() => setShowCost(v => !v)} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                {showCost ? 'Hide cost column' : `Show cost column (internal)${costCount > 0 ? ` -- ${costCount} line${costCount === 1 ? '' : 's'} have a cost` : ''}`}
              </button>
            </div>
            {/* min-w-max: the table never squeezes below its columns' own widths -- it scrolls
                sideways instead (the pinned Charge Code column keeps the row identifiable). */}
            <div className="overflow-x-auto" {...lineScrollProps}>
              <table ref={lineTableRef as React.RefObject<HTMLTableElement>} className="w-full min-w-max text-xs border-separate border-spacing-0">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <th className={`px-2 py-2 ${stickyCodeCell} min-w-[16rem]`}>Charge Code</th>
                    <th className="px-2 py-2 min-w-[9rem]">Unit</th>
                    <RateLineExtraHeaders />
                    {showContainerType && <th className="px-2 py-2 min-w-[6rem]">Container</th>}
                    <th className="px-2 py-2 min-w-[7.5rem]">Unit Price</th>
                    {showCost && <th className="px-2 py-2 min-w-[7.5rem]">Cost</th>}
                    <th className="px-2 py-2 min-w-[7rem]">Min</th>
                    <th className="px-2 py-2 min-w-[7rem]">Max</th>
                    <th className="px-2 py-2 min-w-[14rem]">Notes</th>
                    <th className="px-2 py-2">Tiers</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {lines.map(line => (
                    <Fragment key={line._key}>
                    <tr className={line.is_enabled ? '' : '[&>td:not(:first-child)]:opacity-50'}>
                      <td className={`px-2 py-2 ${stickyCodeCell}`}>
                        <select className={cellInputCls + (line.is_enabled ? '' : ' opacity-50')} title={chargeCodes.find(c => c.id === line.charge_code_id)?.code || ''} value={line.charge_code_id} onChange={e => updateLine(line._key, { charge_code_id: e.target.value })}>
                          <option value="">—</option>
                          {chargeCodes.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <select className={cellInputCls} title={line.unit} value={line.unit} onChange={e => updateLine(line._key, { unit: e.target.value as RateLineUnit })}>
                          {RATE_LINE_UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </td>
                      <RateLineExtraCells line={line} onChange={patch => updateLine(line._key, patch.is_at_cost ? { ...patch, unit_price: null } : patch)} />
                      {showContainerType && (
                        <td className="px-2 py-2">
                          <select className={cellInputCls} value={line.container_type_id || ''} onChange={e => updateLine(line._key, { container_type_id: e.target.value || null })}>
                            <option value="">—</option>
                            {containerTypes.map(ct => <option key={ct.id} value={ct.id}>{ct.code}</option>)}
                          </select>
                        </td>
                      )}
                      <td className="px-2 py-2">
                        <input type="number" step="0.01" disabled={line.is_at_cost} className={cellInputCls} value={line.unit_price ?? ''} placeholder={line.is_at_cost ? 'at cost' : 'not set'}
                          onChange={e => updateLine(line._key, { unit_price: e.target.value === '' ? null : parseFloat(e.target.value) })} />
                      </td>
                      {showCost && (
                        <td className="px-2 py-2">
                          <input type="number" step="0.01" className={cellInputCls + ' bg-amber-50 dark:bg-amber-900/20'} value={line.unit_cost ?? ''} onChange={e => updateLine(line._key, { unit_cost: e.target.value === '' ? null : parseFloat(e.target.value) || 0 })} placeholder="internal" />
                        </td>
                      )}
                      <td className="px-2 py-2">
                        <input type="number" step="0.01" className={cellInputCls} value={line.min_charge ?? ''} onChange={e => updateLine(line._key, { min_charge: e.target.value === '' ? null : parseFloat(e.target.value) || 0 })} />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" step="0.01" className={cellInputCls} value={line.max_charge ?? ''} onChange={e => updateLine(line._key, { max_charge: e.target.value === '' ? null : parseFloat(e.target.value) || 0 })} />
                      </td>
                      <td className="px-2 py-2">
                        <input className={cellInputCls} value={line.notes || ''} onChange={e => updateLine(line._key, { notes: e.target.value })} />
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <button type="button" onClick={() => toggleTiers(line._key)} className="text-blue-600 dark:text-blue-400 hover:underline">
                          {tiersOpen.has(line._key) ? 'Hide' : 'Tiers'}{line.tiers.length > 0 ? ` (${line.tiers.length})` : ''}
                        </button>
                      </td>
                      <td className="px-2 py-2">
                        <button onClick={() => removeLine(line._key)} className="text-red-500 hover:underline text-xs">Remove</button>
                      </td>
                    </tr>
                    {tiersOpen.has(line._key) && (
                      <tr className="bg-gray-50 dark:bg-gray-700/30">
                        <td colSpan={12 + (showContainerType ? 1 : 0) + (showCost ? 1 : 0)} className="px-4 py-3">
                          {/* sticky: stays in view while the table is scrolled sideways */}
                          <div className="sticky left-0 w-fit max-w-full">
                            <TierEditor tiers={line.tiers} onChange={tiers => updateLine(line._key, { tiers })} unitLabel={line.unit} currency={cardForm.currency} />
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <StickyHScrollbar {...lineScrollBar} fullWidth />
            <button onClick={addLine} className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline">+ Add Line</button>
            <p className="mt-2 text-xs text-gray-400">Cost (amber, hidden by default) is internal-only -- never shown to customers, and only readable by ADMIN/OPERATIONS staff.</p>
          </div>

          <div className="flex justify-end gap-3">
            <button onClick={() => save(false)} disabled={saving} className="px-5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
            <button onClick={() => save(true)} disabled={saving} className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save & Publish'}
            </button>
          </div>
        </div>

        <div className="w-full 2xl:w-64 flex-shrink-0">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white mb-3 uppercase tracking-wide">Version History</h3>
            {!cardId ? (
              <p className="text-xs text-gray-400">Save the card to start tracking history.</p>
            ) : audit.length === 0 ? (
              <p className="text-xs text-gray-400">No history yet.</p>
            ) : (
              <ul className="space-y-3">
                {audit.map(a => (
                  <li key={a.id} className="text-xs">
                    <div className="font-semibold text-gray-700 dark:text-gray-300">{a.operation}</div>
                    <div className="text-gray-400">{new Date(a.created_at).toLocaleString()}</div>
                    <div className="text-gray-400">{a.user_email || 'unknown'}</div>
                    {a.changed_fields && a.changed_fields.length > 0 && (
                      <div className="text-gray-400 mt-0.5">{a.changed_fields.join(', ')}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex flex-wrap justify-between items-end gap-3 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Customer</label>
            {/* Z88: searchable (server-side) instead of a native <select> listing every customer */}
            <div className="flex items-center gap-1 w-64">
              <div className="flex-1"><CustomerCombobox value={filterCustomer} onChange={setFilterCustomer} placeholder="All customers" /></div>
              {filterCustomer && <button type="button" onClick={() => setFilterCustomer('')} className="text-xs text-gray-500 hover:text-gray-800 px-1" title="Show all customers">✕</button>}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Mode</label>
            <select className={inputCls} value={filterMode} onChange={e => setFilterMode(e.target.value)}>
              <option value="">All modes</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name_en}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer pb-2">
            <input type="checkbox" checked={effectiveTodayOnly} onChange={e => setEffectiveTodayOnly(e.target.checked)} className="h-4 w-4 text-blue-600 rounded" />
            <span className="text-sm text-gray-700 dark:text-gray-300">Effective today only</span>
          </label>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowTemplateDialog(true)} disabled={categories.length === 0} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            + New from template
          </button>
          <button onClick={openNew} disabled={categories.length === 0} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
            + Blank rate card
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-gray-400">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                {['Rate Card', 'Customer', 'Mode', 'Clearance', 'Delivery', 'Lane', 'Effective', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {cards.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{c.rate_card_name}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{customerName(c.customer_id)}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{modeName(c.mode_id)}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{c.clearance_type_id ? optionName(clearanceTypes, c.clearance_type_id) : 'Any'}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{c.delivery_method_id ? optionName(deliveryMethods, c.delivery_method_id) : <span className="text-gray-400">n/a</span>}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{laneName(c.lane_id)}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">{c.effective_from} {c.effective_to ? `→ ${c.effective_to}` : '(ongoing)'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      c.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                      c.status === 'DRAFT' ? 'bg-gray-100 text-gray-600' :
                      c.status === 'EXPIRED' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                    }`}>{c.status}</span>
                  </td>
                  <td className="px-4 py-3 space-x-3 whitespace-nowrap">
                    <button onClick={() => openCard(c.id)} className="text-blue-600 hover:underline text-xs font-medium">Edit</button>
                    <button onClick={() => setCloneSource(c)} className="text-blue-600 hover:underline text-xs font-medium" title="Copy this whole card to another customer, or make a new version for the same customer">Copy</button>
                  </td>
                </tr>
              ))}
              {cards.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No rate cards found</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <div className="mt-2 text-xs text-gray-400">{cards.length} rate card{cards.length !== 1 ? 's' : ''}</div>
      {showTemplateDialog && (
        <NewFromTemplateDialog onClose={() => setShowTemplateDialog(false)}
          onCreated={id => { setShowTemplateDialog(false); openCard(id) }} />
      )}
      {cloneSource && (
        <CloneCardDialog source={cloneSource} sourceCustomerName={customerName(cloneSource.customer_id)} onClose={() => setCloneSource(null)}
          onCloned={id => { setCloneSource(null); openCard(id) }} />
      )}
    </div>
  )
}
